import {DistinctChain, DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {GetNDCSchemaConfig_ndcSchemaConfig_NDCSchemaConfigNode as NDCSchemaConfigNode} from 'antibodies/nested-data-creation/panes/__generated/GetNDCSchemaConfig';
import {CodonTranslationTable} from 'biojs/BioConstants';
export enum AntibodyCreationStepKey {
  SELECT_FORMAT = 'SELECT_FORMAT',
  DEFINE_DOMAINS = 'DEFINE_DOMAINS',
  FINALIZE = 'FINALIZE',
}

export const ANTIBODY_CREATION_STEPS = [
  {key: AntibodyCreationStepKey.SELECT_FORMAT, title: 'Select format & components'},
  {key: AntibodyCreationStepKey.DEFINE_DOMAINS, title: 'Configure components'},
  {key: AntibodyCreationStepKey.FINALIZE, title: 'Finalize & create'},
];

export interface SelectFormatPane {
  paneKey: AntibodyCreationStepKey.SELECT_FORMAT;
}

export interface GetDomainsPane {
  paneKey: AntibodyCreationStepKey.DEFINE_DOMAINS;
}

export interface FinalizePane {
  paneKey: AntibodyCreationStepKey.FINALIZE;
}

export type Pane = SelectFormatPane | GetDomainsPane | FinalizePane;

export type NDCSchemaConfig = Omit<NDCSchemaConfigNode, '__typename'>;

/* The below types correspond to the Unprocessed NDC input types in
  benchling/antibodies/nested_data_creation/core/unprocessed_types.py and
  benchling/antibodies/nested_data_creation/core/types.py
  Keep these in sync. */

export interface TranslationParameters {
  geneticCode: CodonTranslationTable;
}

export interface AntibodyComponentAnnotation {
  start: number;
  end: number;
  name: string;
  notes?: string;
  color?: string;
  type?: string;
}

// Represents a new AA sequence that will be created as part of creating an antibody
export interface AntibodyAaComponentNew {
  type: 'NEW';
  residues: string;
  annotations: Array<AntibodyComponentAnnotation>;
  germlineId?: string | null;
}

// Represents a new DNA sequence that will be created as part of creating an antibody
export interface AntibodyDnaComponentNew {
  type: 'NEW';
  bases: string;
  annotations: Array<AntibodyComponentAnnotation>;
  germlineId?: string | null;
}

// Represents a DNA sequence that already exists in the Benchling DB
export interface AntibodyDnaComponentExisting {
  type: 'EXISTING';
  dnaSequenceId: string;
}

// Represents an AA sequence that already exists in the Benchling DB
export interface AntibodyAaComponentExisting {
  type: 'EXISTING';
  aaSequenceId: string;
}

type AntibodyDnaComponent = AntibodyDnaComponentNew | AntibodyDnaComponentExisting;
type AntibodyAaComponent = AntibodyAaComponentNew | AntibodyAaComponentExisting;

export interface AntibodyComponentInputBase {
  dnaComponent: AntibodyDnaComponent | null;
  aaComponent: AntibodyAaComponent | null;
}

// An input to create a single Domain of an antibody.
// Must have at least one of dna_component or aa_component defined when sent to the DAO.
export interface AntibodyDomainInput extends AntibodyComponentInputBase {
  componentFormatKey: DistinctDomainKeyWithChainIndex;
}

// An input to create a single Chain of an antibody.
// Must have at least one of dna_component or aa_component defined when sent to the DAO.
export interface AntibodyChainInput extends AntibodyComponentInputBase {
  componentFormatKey: DistinctChain;
}

// For a given Pair, specifies which distinct domains the hardcoded vh and vl fields map to.
export interface PairKey {
  distinctDomainForPairVh: DistinctDomainKeyWithChainIndex;
  distinctDomainForPairVl: DistinctDomainKeyWithChainIndex;
}

export interface AntibodyDomainPairInput {
  pairKey: PairKey;
  dnaPairId: string | null;
  aaPairId: string | null;
}

// Input for creating a single antibody
export interface AntibodyPairAwareCreateInput {
  pairs: Array<AntibodyDomainPairInput> | null;
  domains: Array<AntibodyDomainInput> | null;
  chains: Array<AntibodyChainInput> | null;
}

export interface NestedDataCreationInputConfig {
  antibodyCreateInputs: Array<AntibodyPairAwareCreateInput>;
  // Contains the constant domains for the antibody format. If constant domains are toggled as shared across
  // all antibodies, they are specified here instead of within the antibodyCreateInputs. Afterwards, when
  // submitting to the DAO, these constant domains will be added to each antibodyCreateInputs domains field.
  constantDomains: Array<AntibodyDomainInput>;
  isSharingConstantDomains: boolean;
}

// The types of data that can be created from or used as a starting point in nested data creation
export enum NestedDataType {
  DNA_PAIR = 'DNA_PAIR',
  AA_PAIR = 'AA_PAIR',
  DNA_DOMAIN = 'DNA_DOMAIN',
  AA_DOMAIN = 'AA_DOMAIN',
  DNA_CHAIN = 'DNA_CHAIN',
  AA_CHAIN = 'AA_CHAIN',
}

// The state representing the progress of and configuration specified in the nested data creation modal
export interface AntibodyNestedDataCreationState {
  // The format API ID of antibodies to be created
  selectedFormatId: string | null;
  // The schema API ID for the antibodies to be created
  selectedSchemaId: string | null;
  // The project folder API ID for the antibodies to be created
  selectedProjectFolderId: string | null;
  // The registry API ID
  selectedRegistryId: string | null;
  // The schema configuration for the NDC feature providing the schema IDs for the domains and pairs
  ndcSchemaConfig: NDCSchemaConfig;
  // The configuration describing existing entities that are used in nested data creation
  // This is a light wrapper around antibodyCreateInputs that allows shared constant domains to be normalized
  inputConfig: NestedDataCreationInputConfig;
  // The normalized configuration describing how DNA components will be translated into AA components
  translationParameters: TranslationParameters;
  // The type of nested data that the antibody creation will be derived from
  startingDataType: NestedDataType;
  // Types of entities to create from nested data creation, aside from antibodies which are always created
  outputTypes: ReadonlySet<NestedDataType>;
}

export const INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS: TranslationParameters = {
  geneticCode: CodonTranslationTable.STANDARD,
};

export const INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE: AntibodyNestedDataCreationState = {
  selectedFormatId: null,
  selectedSchemaId: null,
  selectedProjectFolderId: null,
  selectedRegistryId: null,
  ndcSchemaConfig: {
    aaDomainSchemaId: null,
    dnaPairSchemaId: null,
  },
  inputConfig: {
    antibodyCreateInputs: [],
    constantDomains: [],
    isSharingConstantDomains: true,
  },
  translationParameters: INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
  startingDataType: NestedDataType.DNA_DOMAIN,
  outputTypes: new Set([NestedDataType.AA_DOMAIN, NestedDataType.AA_CHAIN]),
};

export const MAX_ANTIBODIES_TO_CREATE = 250;
export const DNA_PAIR_TAG_SCHEMA_SYSTEM_NAME = 'dna_pair';
// Only formats with a single variable pair are currently supported in NDC
export const ALLOWED_ANTIBODY_FORMAT_IDS = [
  'builtinabfmt_Fab',
  'builtinabfmt_IgGmono',
  'builtinabfmt_scFv',
  'builtinabfmt_TCR',
];

// The following types ending in dict should be kept in sync with
// benchling/antibodies/nested_data_creation/core/unprocessed_types.py
// These types omit the chainIndex field that is present in the frontend types, which is used for coloring the
// chains and domains in the UI only. It also updates omits all chain key fields except the label.
export interface DistinctDomainKeyDict {
  label: string;
}

export interface DistinctChainKeyDict {
  label: string;
}

export interface AntibodyPairAwareCreateInputDict {
  pairs: Array<
    Omit<AntibodyDomainPairInput, 'pairKey'> & {
      pairKey: {
        distinctDomainForPairVh: DistinctDomainKeyDict;
        distinctDomainForPairVl: DistinctDomainKeyDict;
      };
    }
  > | null;
  domains: Array<Omit<AntibodyDomainInput, 'componentFormatKey'> & {domainKey: DistinctDomainKeyDict}> | null;
  chains: Array<Omit<AntibodyChainInput, 'componentFormatKey'> & {chainKey: DistinctChainKeyDict}> | null;
}
